import { useEffect } from 'react'
import { useNovu } from '@novu/react'
import { useQueryClient } from '@tanstack/react-query'
import cacheInvalidationGraph from '../../shared/queries/cacheInvalidationGraph'

export type NotificationResult = {
  subject?: string
  body?: string
  data?: Record<string, unknown>
}

type NotificationObject = { type: string }

export const parseObjectType = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null
  try {
    const parsed = JSON.parse(raw) as NotificationObject
    return typeof parsed.type === 'string' ? parsed.type : null
  } catch {
    return null
  }
}

const useNovuQueryInvalidation = () => {
  const novu = useNovu()
  const queryClient = useQueryClient()

  useEffect(() => {
    const handleReceived = ({ result }: { result: NotificationResult }) => {
      const objectType = parseObjectType(result.data?.object)
      if (!objectType) return
      const invalidation = cacheInvalidationGraph[objectType]
      if (!invalidation) return

      invalidation.refetch.forEach(queryKey => queryClient.invalidateQueries({ queryKey }))
      invalidation.stale.forEach(queryKey => queryClient.invalidateQueries({ queryKey, refetchType: 'none' }))
    }

    const cleanup = novu.on('notifications.notification_received', handleReceived)
    return () => {
      cleanup()
    }
  }, [novu, queryClient])
}

export default useNovuQueryInvalidation
